'use client';

import { useMemo, useState } from 'react';
import { Check, Church, Home, Plane } from 'lucide-react';
import { SheetPage, useResponsiveSheet } from '@church/nextjs-ui/components/ResponsiveSheet';
import { campusSvgMap } from '@church/nextjs-ui/website';
import { campusSlug, setSelectedCampus } from '@/lib/campus';
import type { Address, AddressKind, Congregation, LocationPlace } from '@/lib/types';
import { useProfile } from './ProfileContext';
import { CampusIcon } from './CampusIcon';
import { Form, TextField } from './forms';
import { HomeMap, type MapPinSpec } from './HomeMap';
import { DISPLAY, Fact, FactCard, FactGrid, PageBody, Pii, ProfileSheet, Row, RowCard } from './ui';

/**
 * Where the household is, as one card on the details grid: the map edge to
 * edge on top (home, the seasonal address and the campus pinned), the
 * three facts under it. Tap anywhere and the Home sheet opens: the map
 * again, then a row for each fact that drills into its editor (Back
 * returns to the list). The campus page writes the firm field
 * (Households.Congregation_ID) and the header picker's field, then fires
 * `campus-picker:changed`, exactly as the finders do.
 */
export function HomeCard({
  locations,
  mapsApiKey,
}: {
  locations: LocationPlace[];
  mapsApiKey: string | undefined;
}) {
  const { data } = useProfile();
  const { household } = data;
  const [open, setOpen] = useState(false);
  const campusId = household?.congregationId ?? null;

  const campusPlace = useMemo(
    () =>
      campusId
        ? (locations.find((l) => l.congregations.some((c) => c.id === campusId)) ?? null)
        : null,
    [locations, campusId]
  );

  const pins = useMemo<MapPinSpec[]>(() => {
    const out: MapPinSpec[] = [];
    const line = (a: Address) =>
      `${a.line1}${a.line2 ? `, ${a.line2}` : ''}, ${a.city}, ${a.state} ${a.zip}`;
    if (household?.address)
      out.push({
        key: 'home',
        kind: 'home',
        lat: household.address.lat,
        lng: household.address.lng,
        address: line(household.address),
        title: 'Home',
      });
    if (household?.altAddress)
      out.push({
        key: 'alt',
        kind: 'seasonal',
        lat: household.altAddress.lat,
        lng: household.altAddress.lng,
        address: line(household.altAddress),
        title: 'Seasonal address',
      });
    if (campusPlace && campusPlace.latitude && campusPlace.longitude)
      out.push({
        key: 'campus',
        kind: 'campus',
        lat: campusPlace.latitude,
        lng: campusPlace.longitude,
        title: household?.congregationName ?? 'Campus',
        svgUrl: campusPlace.svg_url,
      });
    return out;
  }, [household, campusPlace]);

  if (!household) return null;
  const address = household.address;
  const alt = household.altAddress;

  // Fills whatever box it is put in: the card's header, the sheet's frame. The
  // pins say what they are (Colton: no legend pills, "just the pins are enough").
  const map = (
    <div className="relative h-full w-full overflow-hidden bg-gray-100">
      <HomeMap
        apiKey={mapsApiKey}
        pins={pins}
        className="pii pointer-events-none absolute inset-0 h-full w-full"
      />
    </div>
  );

  return (
    <>
      <FactCard
        title="Home"
        icon={<Home className="h-4 w-4" strokeWidth={2.5} />}
        onEdit={() => setOpen(true)}
        editLabel="Open"
        header={<div className="h-40 shrink-0">{map}</div>}
        actionOnHeader
      >
        <FactGrid>
          <Fact
            label="Home address"
            className="@xs:col-span-2"
            value={address ? <AddressLines address={address} /> : null}
          />
          <Fact
            label="Seasonal address"
            value={alt ? <AddressLines address={alt} /> : null}
            missing="None"
          />
          <Fact
            label="Campus"
            value={
              household.congregationName ? (
                <span className="inline-flex items-center gap-1.5">
                  <CampusIcon congregationId={campusId} className="h-5 w-5" />
                  {household.congregationName}
                </span>
              ) : null
            }
            missing="Choose"
          />
        </FactGrid>
      </FactCard>

      <ProfileSheet open={open} onClose={() => setOpen(false)} title="Home" maxWidth="max-w-2xl">
        <SheetPage name="main">
          <HomePage map={map} address={address} alt={alt} />
        </SheetPage>
        <SheetPage name="primary" title="Home address">
          <AddressPage kind="primary" address={address} />
        </SheetPage>
        <SheetPage name="alt" title="Seasonal address">
          <AddressPage kind="alt" address={alt} />
        </SheetPage>
        <SheetPage name="campus" title="Your campus">
          <CampusPage locations={locations} />
        </SheetPage>
      </ProfileSheet>
    </>
  );
}

function AddressLines({ address }: { address: Address }) {
  return (
    <Pii className="block">
      <span className="block">
        {address.line1}
        {address.line2 ? `, ${address.line2}` : ''}
      </span>
      <span className="block">
        {[address.city, address.state].filter(Boolean).join(', ')}
        {address.zip ? ` ${address.zip}` : ''}
      </span>
    </Pii>
  );
}

/** The sheet's first page: the map, then the three facts as rows that drill into their editors. */
function HomePage({
  map,
  address,
  alt,
}: {
  map: React.ReactNode;
  address: Address | null;
  alt: Address | null;
}) {
  const { data } = useProfile();
  const { navigate } = useResponsiveSheet();
  const household = data.household;
  const oneLine = (a: Address) =>
    `${a.line1}${a.line2 ? `, ${a.line2}` : ''}, ${a.city}, ${a.state} ${a.zip}`;
  return (
    <PageBody>
      <div className="h-56 overflow-hidden rounded-3xl ring-1 ring-gray-200">{map}</div>
      <RowCard>
        <Row
          label="Home address"
          value={address ? <Pii>{oneLine(address)}</Pii> : 'Add'}
          muted={!address}
          onClick={() => navigate('primary')}
          leading={<Home className="h-4 w-4 shrink-0 text-[#299c8f]" strokeWidth={2.5} />}
        />
        <Row
          label="Seasonal address"
          value={alt ? <Pii>{oneLine(alt)}</Pii> : 'Add one'}
          muted={!alt}
          onClick={() => navigate('alt')}
          leading={<Plane className="h-4 w-4 shrink-0 text-[#299c8f]" strokeWidth={2.5} />}
        />
        <Row
          label="Campus"
          value={household?.congregationName ?? 'Choose'}
          muted={!household?.congregationName}
          onClick={() => navigate('campus')}
          leading={<Church className="h-4 w-4 shrink-0 text-[#299c8f]" strokeWidth={2.5} />}
        />
      </RowCard>
      <p className="text-xs text-gray-500">
        Everyone in the household shares the addresses. Mail follows you to the seasonal one when
        the office knows the dates.
      </p>
    </PageBody>
  );
}

/** An address editor as a page: saving goes back to the list. */
function AddressPage({ kind, address }: { kind: AddressKind; address: Address | null }) {
  const { goBack } = useResponsiveSheet();
  return <AddressForm kind={kind} address={address} onDone={goBack} />;
}

function AddressForm({
  kind,
  address,
  onDone,
}: {
  kind: AddressKind;
  address: Address | null;
  onDone: () => void;
}) {
  const { data, api, run, update } = useProfile();
  const [line1, setLine1] = useState(address?.line1 ?? '');
  const [line2, setLine2] = useState(address?.line2 ?? '');
  const [city, setCity] = useState(address?.city ?? '');
  const [state, setState] = useState(address?.state ?? 'MI');
  const [zip, setZip] = useState(address?.zip ?? '');
  const valid =
    line1.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0 &&
    zip.trim().length >= 5;

  const submit = () => {
    // No coordinates yet: MP geocodes later, the map geocodes now.
    const next: Address = {
      line1: line1.trim(),
      line2: line2.trim() || null,
      city: city.trim(),
      state: state.trim().toUpperCase(),
      zip: zip.trim(),
      lat: null,
      lng: null,
    };
    const field = kind === 'alt' ? 'altAddress' : 'address';
    const snapshot = data;
    update((d) => (d.household ? { ...d, household: { ...d.household, [field]: next } } : d));
    onDone();
    void run(
      async () => {
        const saved = await api.updateAddress(next, kind);
        update((d) => (d.household ? { ...d, household: { ...d.household, [field]: saved } } : d));
      },
      () => update(() => snapshot)
    );
  };

  return (
    <Form onSubmit={submit} isValid={valid}>
      <TextField
        label="Street address"
        value={line1}
        onChange={setLine1}
        required
        autoComplete="address-line1"
        autoFocus
      />
      <TextField
        label="Apartment, suite"
        value={line2}
        onChange={setLine2}
        autoComplete="address-line2"
      />
      <div className="grid grid-cols-1 gap-4 @md:grid-cols-[1fr_5rem_8rem]">
        <TextField
          label="City"
          value={city}
          onChange={setCity}
          required
          autoComplete="address-level2"
        />
        <TextField
          label="State"
          value={state}
          onChange={setState}
          required
          autoComplete="address-level1"
          maxLength={2}
        />
        <TextField
          label="ZIP"
          value={zip}
          onChange={setZip}
          required
          autoComplete="postal-code"
          inputMode="numeric"
          maxLength={10}
        />
      </div>
    </Form>
  );
}

/** The campuses as tiles with their marks; one tap picks and goes back to the list. */
function CampusPage({ locations }: { locations: LocationPlace[] }) {
  const { data, api, run, update, mock } = useProfile();
  const { goBack } = useResponsiveSheet();
  const campusId = data.household?.congregationId ?? null;

  const choose = (c: Congregation) => {
    goBack();
    if (c.id === campusId) return;
    const snapshot = data;
    update((d) =>
      d.household
        ? {
            ...d,
            household: { ...d.household, congregationId: c.id, congregationName: c.name },
            me: { ...d.me, webCongregationId: c.id },
          }
        : d
    );
    if (!mock) {
      setSelectedCampus({
        congregationId: c.id,
        name: c.name,
        slug: c.slug ?? campusSlug(c.name),
        svgUrl: campusSvgMap(locations).get(c.id) ?? null,
      });
    }
    void run(
      async () => {
        await api.setHouseholdCampus(c.id);
        await api.setHomeCampus(c.id);
      },
      () => update(() => snapshot),
      `Your campus is now ${c.name}`
    );
  };

  return (
    <PageBody>
      <p className="text-sm text-gray-600">
        Where your household attends. Your campus pastor and team reach you from here.
      </p>
      <div className="grid grid-cols-2 gap-3 @md:grid-cols-3">
        {data.lookups.congregations.map((c) => {
          const selected = c.id === campusId;
          const place = locations.find((l) => l.congregations.some((x) => x.id === c.id));
          return (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => choose(c)}
              className={`relative flex flex-col items-center gap-2 rounded-3xl px-3 py-4 text-center transition-all hover:scale-[1.03] hover:shadow-md active:scale-[0.98] ${
                selected
                  ? 'bg-white shadow-md ring-2 ring-[#299c8f]'
                  : 'bg-white ring-1 ring-gray-200'
              }`}
            >
              {selected && (
                <span className="absolute top-2.5 right-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#299c8f] text-white">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
              )}
              <CampusIcon congregationId={c.id} className="h-12 w-12" />
              <span className="min-w-0">
                <span className={`${DISPLAY} block text-xl leading-none text-[#1c2b39]`}>
                  {c.name.replace(/\s+campus$/i, '')}
                </span>
                {place?.city && (
                  <span className="mt-1 block text-[11px] text-gray-500">{place.city}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </PageBody>
  );
}
